import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus, Edit3, Trash2, Search, Filter, Upload, Download,
  ShieldCheck, BookOpen, CheckCircle2, Clock, XCircle, FileQuestion,
  Check, Minus, Paperclip, X, ChevronDown, FileDown, FileUp, ArrowRightLeft,
} from "lucide-react";
import * as XLSX from "xlsx";
import api, { API, getToken } from "@/lib/api";
import EmployeeForm from "@/components/EmployeeForm";
import EmployeeDetails from "@/components/EmployeeDetails";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { formatHijri, statusOf, daysUntilHijri } from "@/lib/hijri";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { usePerms } from "@/lib/perms";
import { toast } from "sonner";

const STATUS_ICON = {
  valid: { cls: "status-valid", Icon: CheckCircle2 },
  warning: { cls: "status-warning", Icon: Clock },
  expired: { cls: "status-expired", Icon: XCircle },
  missing: { cls: "status-missing", Icon: FileQuestion },
};

function StatusBadge({ hijri, t }) {
  const s = statusOf(hijri);
  const cfg = STATUS_ICON[s];
  const d = daysUntilHijri(hijri);
  let suffix = "";
  if (s === "warning") suffix = ` (${d}${t("days_short")})`;
  if (s === "expired") suffix = ` (${Math.abs(d)}${t("days_short")})`;
  return (
    <span className={`pill ${cfg.cls}`}>
      <cfg.Icon size={12} /> {t(`status_${s}`)}{suffix}
    </span>
  );
}

function BoolPill({ value, t }) {
  return value ? (
    <span className="pill status-valid"><Check size={12} /> {t("yes")}</span>
  ) : (
    <span className="pill status-missing"><Minus size={12} /> {t("no")}</span>
  );
}

export default function Employees() {
  const { t } = useT();
  const { user } = useAuth();
  const { can } = usePerms();
  const canEdit = can("add_employees");
  const canAdd = can("add_employees");
  const canDelete = can("delete_employees");
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [branchId, setBranchId] = useState(params.get("branch_id") || "");
  const [adminId, setAdminId] = useState(params.get("admin_id") || "");
  const [areaManagerId, setAreaManagerId] = useState(params.get("area_manager_id") || "");
  const [areaManagers, setAreaManagers] = useState([]);
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "");
  const [certType, setCertType] = useState(params.get("cert") || "health");
  const [adminCategory, setAdminCategory] = useState(params.get("category") === "admin");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (params.get("add") === "1") setEditing({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [viewing, setViewing] = useState(null);              // employee whose details modal is open
  const [attachingFor, setAttachingFor] = useState(null);   // employee whose attachments are being viewed
  const fileRef = useRef(null);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const importMenuRef = useRef(null);
  const [movingEmp, setMovingEmp] = useState(null);
  const [moveEmpTarget, setMoveEmpTarget] = useState("");
  const [moveEmpTargetAdmin, setMoveEmpTargetAdmin] = useState("");
  const [moveEmpTargetBranch, setMoveEmpTargetBranch] = useState("");
  const [moveEmpError, setMoveEmpError] = useState("");
  const [areaManagersLite, setAreaManagersLite] = useState([]);
  const [moveAdminsList, setMoveAdminsList] = useState([]);
  const [moveBranchesList, setMoveBranchesList] = useState([]);
  const canMove = user?.role === "super_admin" || user?.role === "area_manager" || user?.role === "supervisor";
  const [transfers, setTransfers] = useState({ incoming: [], outgoing: [], pending_count: 0 });
  const [transfersOpen, setTransfersOpen] = useState(false);
  const [decidingId, setDecidingId] = useState(null);

  const loadTransfers = async () => {
    try {
      const r = await api.get("/transfers");
      setTransfers(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadTransfers(); }, []);

  const decideTransfer = async (tid, approve) => {
    setDecidingId(tid);
    try {
      await api.post(`/transfers/${tid}/${approve ? "approve" : "reject"}`);
      toast.success(approve ? t("transfer_approved_toast") : t("transfer_rejected_toast"));
      await Promise.all([loadTransfers(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("save_failed"));
    } finally { setDecidingId(null); }
  };

  useEffect(() => {
    if (user?.role !== "super_admin" && user?.role !== "area_manager" && user?.role !== "supervisor") return;
    api.get("/area-managers/peers").then((r) => setAreaManagersLite(r.data || [])).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    if (movingEmp && !moveEmpTarget && user?.role === "supervisor") {
      setMoveEmpTarget(user.area_manager_id || "");
    }
    // eslint-disable-next-line
  }, [movingEmp]);

  // Merge lite peers into areaManagerMap so admin-branch rows can show the AM name for all roles
  const peersMap = useMemo(
    () => Object.fromEntries((areaManagersLite || []).map((am) => [am.id, am.full_name || am.username])),
    [areaManagersLite],
  );

  // For supervisors we skip the AM dropdown (they can only move within their own area)
  // For area_manager/super, when the AM changes we load that AM's admins & branches
  useEffect(() => {
    if (!movingEmp) return;
    const targetAmId = moveEmpTarget || (user?.role === "supervisor" ? user.area_manager_id : "");
    if (!targetAmId) {
      setMoveAdminsList([]);
      setMoveBranchesList([]);
      return;
    }
    (async () => {
      try {
        const [ad, br] = await Promise.all([
          api.get(`/admins/by-area/${targetAmId}`),
          api.get(`/branches/by-area/${targetAmId}`),
        ]);
        setMoveAdminsList(ad.data || []);
        setMoveBranchesList(br.data || []);
        // Reset dependent selects
        setMoveEmpTargetAdmin("");
        setMoveEmpTargetBranch("");
      } catch { setMoveAdminsList([]); setMoveBranchesList([]); }
    })();
  }, [moveEmpTarget, movingEmp, user?.role, user?.area_manager_id]);

  // Close import menu on outside click
  useEffect(() => {
    if (!importMenuOpen) return;
    const onDown = (e) => {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target)) {
        setImportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [importMenuOpen]);

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);
  const areaManagerMap = useMemo(
    () => Object.fromEntries((areaManagers || []).map((am) => [am.id, am.full_name || am.username])),
    [areaManagers],
  );
  const adminMap = useMemo(() => Object.fromEntries(admins.map((a) => [a.id, a.name])), [admins]);

  const load = async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (q) sp.set("q", q);
      if (branchId) sp.set("branch_id", branchId);
      if (adminId) sp.set("admin_id", adminId);
      if (areaManagerId && user?.role === "super_admin") sp.set("area_manager_id", areaManagerId);
      const res = await api.get(`/employees?${sp.toString()}`);
      setItems(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const [br, ad] = await Promise.all([api.get("/branches"), api.get("/admins")]);
      setBranches(br.data); setAdmins(ad.data);
      if (user?.role === "super_admin") {
        try {
          const am = await api.get("/area-managers");
          setAreaManagers(am.data || []);
        } catch { /* ignore */ }
      }
    })();
  }, [user?.role]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, branchId, adminId, areaManagerId]);

  const filtered = useMemo(() => {
    let out = items;
    if (adminCategory) {
      const adminBranchIds = new Set(branches.filter((b) => b.is_admin).map((b) => b.id));
      out = out.filter((e) => adminBranchIds.has(e.branch_id));
    }
    if (statusFilter) {
      out = out.filter((e) => {
        const h = certType === "training" ? e.training_expiry_hijri : e.health_expiry_hijri;
        return statusOf(h) === statusFilter;
      });
    }
    return out;
  }, [items, statusFilter, certType, adminCategory, branches]);

  const remove = async (e) => {
    if (!window.confirm(t("delete_emp_confirm", { name: e.name }))) return;
    await api.delete(`/employees/${e.id}`);
    load();
  };

  const exportCSV = () => {
    const token = getToken();
    fetch(`${API}/employees/export/xlsx`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cert_tracker.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const downloadTemplate = () => {
    const token = getToken();
    fetch(`${API}/employees/template/xlsx`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cert_tracker_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const parseHijriStr = (s) => {
    if (!s) return null;
    const str = String(s).trim();
    const parts = str.split("/");
    if (parts.length !== 3) return null;
    const d = Number(parts[0]), m = Number(parts[1]), y = Number(parts[2]);
    if (!d || !m || !y) return null;
    return { y, m, d };
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data);
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
    if (rows.length === 0) { alert(t("file_empty")); return; }

    const branchByName = Object.fromEntries(branches.map((b) => [String(b.name).trim(), b.id]));
    const branchByCode = Object.fromEntries(branches.filter((b) => b.code).map((b) => [String(b.code).trim(), b.id]));
    const adminByName = Object.fromEntries(admins.map((a) => [String(a.name).trim(), a.id]));

    // Pick first non-empty value across possible header aliases (AR + EN)
    const pick = (r, ...keys) => {
      for (const k of keys) {
        if (r[k] !== undefined && r[k] !== null && String(r[k]).trim() !== "") return r[k];
      }
      return "";
    };

    const employees = rows.map((r) => {
      const bcode = String(pick(r, "كود الفرع", "Branch Code", "branch_code")).trim();
      const bname = String(pick(r, "اسم الفرع", "Branch Name", "branch_name")).trim();
      const branch_id = branchByCode[bcode] || branchByName[bname] || null;
      const supervisorName = String(pick(r, "المشرف", "Supervisor")).trim();
      const muni = String(pick(r, "كرد البلدية", "Municipality Card")).trim().toLowerCase();
      const photo = String(pick(r, "صورة مطبوعة", "Printed Photo")).trim().toLowerCase();
      return {
        employee_id: String(pick(r, "الرقم الوظيفي", "Employee No.", "Employee No", "employee_id")).trim(),
        name: String(pick(r, "الاسم الكامل", "Full Name", "name")).trim(),
        position: String(pick(r, "المنصب", "Position")).trim(),
        iqama_number: String(pick(r, "رقم الإقامة", "Iqama Number", "iqama_number")).trim(),
        phone: String(pick(r, "رقم الجوال", "Mobile", "phone")).trim(),
        branch_id,
        admin_id: adminByName[supervisorName] || null,
        health_expiry_hijri: parseHijriStr(pick(r, "تاريخ انتهاء الشهادة الصحية", "Health Cert Expiry (Hijri)", "Health Expiry")),
        training_expiry_hijri: parseHijriStr(pick(r, "تاريخ انتهاء التثقيف", "Training Expiry (Hijri)", "Training Expiry")),
        municipality_card: muni === "نعم" || muni === "yes" || muni === "true",
        printed_photo: photo === "نعم" || photo === "yes" || photo === "true",
        notes: String(pick(r, "ملاحظات", "Notes")).trim(),
      };
    }).filter((x) => x.name);

    if (employees.length === 0) { alert(t("no_valid_rows")); return; }

    try {
      const res = await api.post("/employees/bulk", { employees });
      const ins = res.data.inserted || 0;
      const upd = res.data.updated || 0;
      const total = res.data.total || (ins + upd);
      alert(t("import_result", { total, inserted: ins, updated: upd }));
      fileRef.current.value = "";
      load();
    } catch (err) {
      alert(t("import_failed") + (err.response?.data?.detail || err.message));
    }
  };

  useEffect(() => {
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (branchId) sp.set("branch_id", branchId);
    if (adminId) sp.set("admin_id", adminId);
    if (areaManagerId && user?.role === "super_admin") sp.set("area_manager_id", areaManagerId);
    if (statusFilter) sp.set("status", statusFilter);
    if (certType !== "health") sp.set("cert", certType);
    if (adminCategory) sp.set("category", "admin");
    setParams(sp, { replace: true });
  }, [q, branchId, adminId, areaManagerId, statusFilter, certType, adminCategory, setParams, user?.role]);

  // Sync adminCategory with the URL so a fresh navigation ?category=admin activates it
  useEffect(() => {
    setAdminCategory(params.get("category") === "admin");
  }, [params]);

  return (
    <div className="space-y-4" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-teal-900 flex items-center gap-2">
            {adminCategory ? (
              <>
                <ShieldCheck size={22} className="text-slate-700" />
                {t("nav_admin_certs")}
              </>
            ) : (
              t("nav_employees")
            )}
          </h2>
          <p className="text-gray-500 text-sm">
            {adminCategory ? t("admin_certs_subtitle") : t("employees_subtitle")}
          </p>
          {adminCategory && (
            <button
              className="text-xs text-teal-700 hover:underline mt-1"
              onClick={() => setAdminCategory(false)}
              data-testid="clear-admin-category"
            >
              ← {t("view_all_employees")}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {(user?.role === "area_manager" || user?.role === "super_admin" || transfers.outgoing.length > 0) && (
            <button
              className="btn btn-secondary relative"
              onClick={() => setTransfersOpen(true)}
              data-testid="transfers-button"
            >
              <ArrowRightLeft size={16} /> {t("transfers_title")}
              {transfers.pending_count > 0 && (
                <span className="absolute -top-1.5 -end-1.5 bg-rose-600 text-white text-[10px] font-black rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="transfers-pending-badge">
                  {transfers.pending_count}
                </span>
              )}
            </button>
          )}
          {canEdit && (
            <input type="file" accept=".xlsx,.xls" className="hidden" ref={fileRef} onChange={handleImport} data-testid="import-file-input" />
          )}
          {canEdit && (
            <div className="relative" ref={importMenuRef}>
              <button
                className="btn btn-secondary"
                onClick={() => setImportMenuOpen((v) => !v)}
                data-testid="import-excel-button"
              >
                <Upload size={16} /> {t("import_excel")} <ChevronDown size={13} />
              </button>
              {importMenuOpen && (
                <div
                  className="absolute z-30 mt-1 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden animate-fade-up"
                  style={{ insetInlineStart: 0 }}
                  data-testid="import-menu"
                >
                  <button
                    onClick={() => { setImportMenuOpen(false); downloadTemplate(); }}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-teal-50 text-start"
                    data-testid="import-download-template"
                  >
                    <FileDown size={18} className="text-teal-700 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-bold text-teal-900">{t("download_blank_template")}</div>
                      <div className="text-[11px] text-gray-500">{t("download_blank_template_hint")}</div>
                    </div>
                  </button>
                  <div className="border-t border-slate-100" />
                  <button
                    onClick={() => { setImportMenuOpen(false); fileRef.current?.click(); }}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-amber-50 text-start"
                    data-testid="import-upload-file"
                  >
                    <FileUp size={18} className="text-amber-700 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-bold text-amber-900">{t("upload_filled_file")}</div>
                      <div className="text-[11px] text-gray-500">{t("upload_filled_file_hint")}</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
          <button className="btn btn-secondary" onClick={exportCSV} data-testid="export-csv-button">
            <Download size={16} /> {t("export_csv")}
          </button>
          {canAdd && (
            <button className="btn btn-primary" onClick={() => setEditing({})} data-testid="add-emp-button">
              <Plus size={16} /> {t("add_employee")}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl card-shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
            <input
              className="inp"
              style={{ paddingInlineEnd: "2.25rem" }}
              placeholder={t("search_placeholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
            />
          </div>
          {user?.role === "super_admin" && (
            <select className="inp" value={areaManagerId} onChange={(e) => setAreaManagerId(e.target.value)} data-testid="filter-area-manager">
              <option value="">{t("all_area_managers")}</option>
              {areaManagers.map((am) => (
                <option key={am.id} value={am.id}>{am.full_name || am.username}</option>
              ))}
            </select>
          )}
          <select className="inp" value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="filter-branch">
            <option value="">{t("all_branches")}</option>
            {branches.filter((b) => b.is_vacation).map((b) => (
              <option key={b.id} value={b.id}>🏖️ {t("vacation")}</option>
            ))}
            {branches.filter((b) => b.is_admin).map((b) => (
              <option key={b.id} value={b.id}>⚙️ {t("admin_branch_label")}</option>
            ))}
            {branches.filter((b) => !b.is_vacation && !b.is_admin).map((b) => (
              <option key={b.id} value={b.id}>{b.code ? `[${b.code}] ` : ""}{b.name}</option>
            ))}
          </select>
          <select className="inp" value={adminId} onChange={(e) => setAdminId(e.target.value)} data-testid="filter-admin">
            <option value="">{t("all_admins")}</option>
            {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div className="flex gap-2">
            <select className="inp" value={certType} onChange={(e) => setCertType(e.target.value)} data-testid="filter-cert-type">
              <option value="health">{t("cert_health_short")}</option>
              <option value="training">{t("cert_training_short")}</option>
            </select>
            <select className="inp" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
              <option value="">{t("all_statuses")}</option>
              <option value="valid">{t("status_valid")}</option>
              <option value="warning">{t("status_warning")}</option>
              <option value="expired">{t("status_expired")}</option>
              <option value="missing">{t("status_missing")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl card-shadow overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">{t("loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Filter size={36} className="mx-auto mb-3 opacity-50" />
            {t("no_results")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("code_col")}</th>
                  <th>{t("branch")}</th>
                  <th>{t("admin")}</th>
                  <th>{t("employee_id")}</th>
                  <th>{t("name")}</th>
                  <th>{t("position")}</th>
                  <th>{t("iqama_number")}</th>
                  <th className="text-center"><ShieldCheck size={14} className="inline mx-1" />{t("health_expiry_col")}</th>
                  <th className="text-center"><BookOpen size={14} className="inline mx-1" />{t("training_expiry_col")}</th>
                  <th className="text-center">{t("municipality_card")}</th>
                  <th className="text-center">{t("printed_photo")}</th>
                  <th className="text-left">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const b = branchMap[e.branch_id];
                  const onVacation = !!b?.is_vacation;
                  const onAdmin = !!b?.is_admin;
                  return (
                    <tr key={e.id} data-testid={`emp-row-${e.id}`} className={onVacation ? "bg-amber-50/40" : (onAdmin ? "bg-slate-50/60" : "")}>
                      <td className="text-gray-500 text-xs">{b?.code || "—"}</td>
                      <td className="text-gray-700">
                        {onVacation ? (
                          <span className="pill bg-amber-100 text-amber-800 border border-amber-300">
                            🏖️ {t("on_vacation")}
                          </span>
                        ) : onAdmin ? (
                          <span className="pill bg-slate-800 text-white border border-slate-900">
                            ⚙️ {t("admin_branch_label")}
                          </span>
                        ) : (b?.name || "—")}
                      </td>
                      <td className="text-gray-700">
                        {onVacation || onAdmin
                          ? (onAdmin ? (areaManagerMap[e.area_manager_id] || peersMap[e.area_manager_id] || user?.full_name || "—") : "—")
                          : (adminMap[e.admin_id] || "—")}
                      </td>
                      <td className="text-gray-600">{e.employee_id || "—"}</td>
                      <td className="font-semibold">
                        <button
                          type="button"
                          onClick={() => setViewing(e)}
                          className="text-teal-800 hover:text-teal-600 hover:underline focus:outline-none focus:underline cursor-pointer text-start"
                          data-testid={`emp-name-${e.id}`}
                          title={t("view_details")}
                        >
                          {e.name}
                        </button>
                        {(e.attachments || []).length > 0 && (
                          <button
                            type="button"
                            onClick={() => setAttachingFor(e)}
                            className="inline-flex items-center gap-0.5 mr-2 align-middle text-rose-600 hover:text-rose-800 hover:underline"
                            title={`${e.attachments.length} مرفق`}
                            data-testid={`emp-attach-badge-${e.id}`}
                          >
                            <Paperclip size={12} /><span className="text-[10px] font-bold">{e.attachments.length}</span>
                          </button>
                        )}
                      </td>
                      <td className="text-gray-600" style={{direction: "ltr", textAlign: "start"}}>{e.position || "—"}</td>
                      <td className="text-gray-600">{e.iqama_number || "—"}</td>
                      <td className="text-center">
                        <div className="text-sm text-gray-700 mb-1">{formatHijri(e.health_expiry_hijri)}</div>
                        <StatusBadge hijri={e.health_expiry_hijri} t={t} />
                      </td>
                      <td className="text-center">
                        <div className="text-sm text-gray-700 mb-1">{formatHijri(e.training_expiry_hijri)}</div>
                        <StatusBadge hijri={e.training_expiry_hijri} t={t} />
                      </td>
                      <td className="text-center"><BoolPill value={e.municipality_card} t={t} /></td>
                      <td className="text-center"><BoolPill value={e.printed_photo} t={t} /></td>
                      <td className="text-left whitespace-nowrap">
                        <button
                          className="btn btn-ghost"
                          onClick={() => setAttachingFor(e)}
                          title={t("attachments")}
                          data-testid={`attach-emp-${e.id}`}
                        >
                          <Paperclip size={14} />
                          {(e.attachments || []).length > 0 && (
                            <span className="text-xs font-bold text-rose-600">{e.attachments.length}</span>
                          )}
                        </button>
                        {canEdit ? (
                          <button className="btn btn-ghost" onClick={() => setEditing(e)} data-testid={`edit-emp-${e.id}`}>
                            <Edit3 size={14} /> {t("edit")}
                          </button>
                        ) : (
                          <button className="btn btn-ghost" onClick={() => setEditing(e)} data-testid={`edit-emp-${e.id}`}>
                            <Edit3 size={14} /> {t("edit")}
                          </button>
                        )}
                        {canDelete && (
                          <button className="btn btn-danger mr-1" onClick={() => remove(e)} data-testid={`delete-emp-${e.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-3 text-sm text-gray-500 bg-gray-50 border-t border-gray-100">
          {t("total")}: <span className="font-bold text-teal-800">{filtered.length}</span> {t("employees_count_total")}
        </div>
      </div>

      {viewing && (
        <EmployeeDetails
          employee={viewing}
          branch={branchMap[viewing.branch_id]}
          adminName={adminMap[viewing.admin_id]}
          onClose={() => setViewing(null)}
          onEdit={(emp) => { setViewing(null); setEditing(emp); }}
          onOpenAttachments={(emp) => { setViewing(null); setAttachingFor(emp); }}
          onMove={canMove ? (emp) => {
            setViewing(null);
            setMovingEmp(emp);
            setMoveEmpTarget("");
            setMoveEmpError("");
          } : undefined}
        />
      )}

      {editing && (
        <EmployeeForm
          employee={editing}
          branches={branches}
          admins={admins}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {attachingFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in"
          onClick={() => { setAttachingFor(null); load(); }}
          data-testid="attachments-modal"
        >
          <div
            className="bg-white rounded-2xl card-shadow w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-teal-900 flex items-center gap-2">
                  <Paperclip size={16} /> {t("attachments")}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{attachingFor.name}</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { setAttachingFor(null); load(); }}
                data-testid="attachments-modal-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-6">
              <AttachmentsPanel
                employeeId={attachingFor.id}
                attachments={attachingFor.attachments || []}
                onChange={(updated) => {
                  // sync local state so badge counts stay live without full reload
                  setItems((prev) => prev.map((x) => (x.id === attachingFor.id ? { ...x, attachments: updated } : x)));
                  setAttachingFor((prev) => (prev ? { ...prev, attachments: updated } : prev));
                }}
              />
            </div>
          </div>
        </div>
      )}

      {movingEmp && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="move-emp-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowRightLeft size={18} className="text-indigo-600" />
                <h3 className="font-bold text-lg">{t("move_employee_title")}</h3>
              </div>
              <button className="btn btn-ghost" onClick={() => setMovingEmp(null)}><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p className="text-gray-600">{t("move_employee_desc").replace("{name}", movingEmp.name)}</p>

              {/* Area Manager select (hidden for supervisors — locked to their own area) */}
              {user?.role !== "supervisor" && (
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">{t("move_target_area_manager")}</label>
                  <select
                    value={moveEmpTarget}
                    onChange={(e) => setMoveEmpTarget(e.target.value)}
                    className="inp"
                    data-testid="move-emp-target-select"
                  >
                    <option value="">— {t("select_area_manager")} —</option>
                    {areaManagersLite.map((am) => (
                      <option key={am.id} value={am.id}>
                        {am.full_name || am.username} ({am.username})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Supervisor select */}
              {(moveEmpTarget || user?.role === "supervisor") && (
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">{t("target_supervisor")}</label>
                  <select
                    value={moveEmpTargetAdmin}
                    onChange={(e) => setMoveEmpTargetAdmin(e.target.value)}
                    className="inp"
                    data-testid="move-emp-admin-select"
                  >
                    <option value="">— {t("no_supervisor")} —</option>
                    {moveAdminsList.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Branch select */}
              {moveEmpTarget && (
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">{t("target_branch")}</label>
                  <select
                    value={moveEmpTargetBranch}
                    onChange={(e) => setMoveEmpTargetBranch(e.target.value)}
                    className="inp"
                    data-testid="move-emp-branch-select"
                  >
                    <option value="">— {t("no_branch")} —</option>
                    {moveBranchesList
                      .filter((b) =>
                        // If supervisor was picked, only show branches under that supervisor
                        // (plus vacation & admin which are shared). Otherwise show all.
                        !moveEmpTargetAdmin
                          ? true
                          : (b.is_vacation || b.is_admin || b.admin_id === moveEmpTargetAdmin)
                      )
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.code ? `[${b.code}] ` : ""}{b.name}
                          {b.is_admin ? " ⚙️" : ""}
                          {b.is_vacation ? " 🏖️" : ""}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {moveEmpError && (
                <div className="text-sm bg-rose-50 text-rose-700 rounded-lg p-2">{moveEmpError}</div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button className="btn btn-secondary" onClick={() => setMovingEmp(null)}>{t("cancel")}</button>
              <button
                className="btn btn-primary bg-indigo-600 hover:bg-indigo-700"
                data-testid="move-emp-confirm-btn"
                onClick={async () => {
                  const targetAm = moveEmpTarget;
                  if (!targetAm) { setMoveEmpError(t("move_target_required")); return; }
                  const ownAm = user?.role === "area_manager" ? user?.id : user?.area_manager_id;
                  const crossArea = user?.role !== "super_admin" && targetAm !== ownAm;
                  try {
                    const payload = {
                      target_area_manager_id: targetAm,
                      target_admin_id: moveEmpTargetAdmin || null,
                      target_branch_id: moveEmpTargetBranch || null,
                    };
                    if (crossArea) {
                      await api.post("/transfers", { employee_id: movingEmp.id, ...payload });
                      setMovingEmp(null);
                      loadTransfers();
                      toast.success(t("transfer_request_sent"));
                      return;
                    }
                    const res = await api.post(`/employees/${movingEmp.id}/move`, payload);
                    const target = res.data.target?.full_name || "";
                    setMovingEmp(null);
                    load();
                    window.alert(t("move_employee_done").replace("{name}", movingEmp.name).replace("{target}", target));
                  } catch (err) {
                    setMoveEmpError(err.response?.data?.detail || err.message);
                  }
                }}
              >
                <ArrowRightLeft size={14} /> {t("confirm_move")}
              </button>
            </div>
          </div>
        </div>
      )}
      {transfersOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="transfers-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowRightLeft size={18} className="text-indigo-600" />
                <h3 className="font-bold text-lg">{t("transfers_title")}</h3>
              </div>
              <button className="btn btn-ghost" onClick={() => setTransfersOpen(false)} data-testid="transfers-close"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-5 overflow-y-auto text-sm">
              {(user?.role === "area_manager" || user?.role === "super_admin") && (
                <div>
                  <h4 className="text-xs font-black text-slate-500 uppercase mb-2">{t("transfers_incoming")}</h4>
                  {transfers.incoming.length === 0 ? (
                    <p className="text-xs text-slate-400">{t("transfers_none")}</p>
                  ) : (
                    <div className="space-y-2">
                      {transfers.incoming.map((r) => (
                        <div key={r.id} className={`rounded-xl border p-3 ${r.status === "pending" ? "bg-indigo-50 border-indigo-200" : "bg-slate-50 border-slate-200"}`} data-testid={`transfer-row-${r.id}`}>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="min-w-0">
                              <div className="font-bold text-slate-800">{r.employee_name} <span className="text-slate-400 text-[11px]">[{r.employee_code || "—"}]</span></div>
                              <div className="text-[11px] text-slate-600 mt-0.5">
                                {t("transfer_from")}: <b>{r.from_am_name}</b> ← {t("transfer_by")}: {r.requester_name}
                              </div>
                              <div className="text-[11px] text-slate-600">
                                {t("target_supervisor")}: <b>{r.to_admin_name || "—"}</b> · {t("target_branch")}: <b>{r.to_branch_name || "—"}</b>
                              </div>
                              {r.note && <div className="text-[11px] text-amber-800 mt-0.5">📝 {r.note}</div>}
                            </div>
                            {r.status === "pending" ? (
                              <div className="flex gap-2">
                                <button className="btn btn-primary bg-emerald-600 hover:bg-emerald-700 text-xs" disabled={decidingId === r.id}
                                  onClick={() => decideTransfer(r.id, true)} data-testid={`transfer-approve-${r.id}`}>
                                  ✅ {t("transfer_approve")}
                                </button>
                                <button className="btn btn-secondary text-rose-600 text-xs" disabled={decidingId === r.id}
                                  onClick={() => decideTransfer(r.id, false)} data-testid={`transfer-reject-${r.id}`}>
                                  ❌ {t("transfer_reject")}
                                </button>
                              </div>
                            ) : (
                              <span className={`pill ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                {r.status === "approved" ? `✅ ${t("transfer_status_approved")}` : `❌ ${t("transfer_status_rejected")}`}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div>
                <h4 className="text-xs font-black text-slate-500 uppercase mb-2">{t("transfers_outgoing")}</h4>
                {transfers.outgoing.length === 0 ? (
                  <p className="text-xs text-slate-400">{t("transfers_none")}</p>
                ) : (
                  <div className="space-y-2">
                    {transfers.outgoing.map((r) => (
                      <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-bold text-slate-800">{r.employee_name} <span className="text-slate-400 text-[11px]">[{r.employee_code || "—"}]</span></div>
                          <div className="text-[11px] text-slate-600">{t("transfer_to")}: <b>{r.to_am_name}</b> · {r.to_branch_name || "—"}</div>
                        </div>
                        <span className={`pill ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.status === "approved" ? `✅ ${t("transfer_status_approved")}` : r.status === "rejected" ? `❌ ${t("transfer_status_rejected")}` : `⏳ ${t("transfer_status_pending")}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
